import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import React, { ReactElement, ReactNode, useCallback, useMemo } from 'react';
import {
  groupConsecutiveChecklistNodes,
  ItemTreeNode,
  SectionTreeNode,
  TreeNode,
  VisibleChecklistItem,
} from 'shared/domain/checklistTree';
import { TreeRendererProps } from '../types';
import { ACCORDION_UNMOUNT_ON_EXIT_SLOT_PROPS } from '../constants';
import {
  SECTION_ACCORDION_SX,
  SECTION_DETAILS_SX,
  SECTION_NUMBER_SX,
  SECTION_SUMMARY_SX,
  SECTION_TITLE_SX,
  TREE_CHILDREN_SX,
} from '../styles';

function ChecklistTreeRenderer({
  tree,
  expandedIds,
  onExpandedChange,
  renderItem,
  renderItemGroup,
  renderSectionSummaryEnd,
  renderSectionContent,
  resolveSectionTitle,
  sectionSummarySx,
}: TreeRendererProps): ReactElement {
  const renderItemRun = useCallback(
    (
      nodes: ItemTreeNode[],
      itemIndexRef: { current: number }
    ): ReactNode => {
      const items: VisibleChecklistItem[] = nodes.map((node) => {
        const itemIndex = itemIndexRef.current;
        itemIndexRef.current += 1;
        return { node, itemIndex };
      });

      if (items.length === 0) {
        return null;
      }

      if (renderItemGroup) {
        return (
          <React.Fragment key={`items-${items[0].node.id}`}>
            {renderItemGroup(items, renderItem)}
          </React.Fragment>
        );
      }

      return items.map((item) => (
        <React.Fragment key={item.node.id}>
          {renderItem(item.node, item.itemIndex)}
        </React.Fragment>
      ));
    },
    [renderItem, renderItemGroup]
  );

  const renderSection = useCallback(
    (
      node: SectionTreeNode,
      itemIndexRef: { current: number },
      renderNodeList: (
        nodes: TreeNode[],
        itemIndexRef: { current: number }
      ) => ReactNode
    ): ReactElement => {
      const sectionExpanded = expandedIds?.has(node.id) ?? true;

      return (
        <Accordion
          key={node.id}
          expanded={sectionExpanded}
          onChange={(_, isExpanded) =>
            onExpandedChange?.(node.id, isExpanded)
          }
          slotProps={ACCORDION_UNMOUNT_ON_EXIT_SLOT_PROPS}
          sx={SECTION_ACCORDION_SX}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              ...SECTION_SUMMARY_SX,
              ...sectionSummarySx,
            }}
          >
            <Typography
              variant='body1'
              fontWeight={sectionExpanded ? 700 : 400}
              sx={SECTION_NUMBER_SX}
            >
              {node.displayNumber}
            </Typography>
            <Typography
              variant='body1'
              fontWeight={sectionExpanded ? 700 : 400}
              sx={SECTION_TITLE_SX}
            >
              {resolveSectionTitle
                ? resolveSectionTitle(node)
                : node.data.title}
            </Typography>
            {renderSectionSummaryEnd
              ? renderSectionSummaryEnd(node, sectionExpanded)
              : null}
          </AccordionSummary>
          <AccordionDetails sx={SECTION_DETAILS_SX}>
            {sectionExpanded ? (
              <>
                {renderSectionContent
                  ? renderSectionContent(node, sectionExpanded)
                  : null}
                {node.children.length > 0 ? (
                  <Box sx={TREE_CHILDREN_SX}>
                    {renderNodeList(node.children, itemIndexRef)}
                  </Box>
                ) : null}
              </>
            ) : null}
          </AccordionDetails>
        </Accordion>
      );
    },
    [
      expandedIds,
      onExpandedChange,
      renderSectionSummaryEnd,
      renderSectionContent,
      resolveSectionTitle,
      sectionSummarySx,
    ]
  );

  const renderNodeList = useCallback(
    (
      nodes: TreeNode[],
      itemIndexRef: { current: number }
    ): ReactNode => {
      return groupConsecutiveChecklistNodes(nodes).map((group) => {
        if (group.type === 'items') {
          return renderItemRun(group.nodes, itemIndexRef);
        }

        return renderSection(group.node, itemIndexRef, renderNodeList);
      });
    },
    [renderItemRun, renderSection]
  );

  const renderedTree = useMemo(() => {
    const itemIndexRef = { current: 0 };
    return renderNodeList(tree, itemIndexRef);
  }, [tree, renderNodeList]);

  return <>{renderedTree}</>;
}

export const MemoChecklistTreeRenderer = React.memo(ChecklistTreeRenderer);
